import { openAuthModal } from "@blocks/_shared/auth/use-auth-modal";
import apiFetch from "@wordpress/api-fetch";
import { createRoot } from "@wordpress/element";
import { __ } from "@wordpress/i18n";
/**
 * Coupons List Block - Frontend View
 *
 * Displays user's available coupons from spend rules and promotion rules.
 * Layout (per Figma): icon + title + voucher-terms link header row,
 * with a discount-code + copy-code section below.
 */
import type React from "react";
import { type FC, useEffect, useState } from "react";
import { joinProgram } from "~/shared/queries";
import Button from "../_shared/button";
import {
	Item,
	ItemContent,
	ItemGridSkeleton,
	Modal,
} from "../_shared/components";
import { useEnrollment } from "../_shared/hooks/use-enrollment";
import { replaceStrings } from "../_shared/utils/replacers";
import { getTranslatedText } from "../_shared/utils/translated-text";
import "./style.scss";

// ============================================================================
// Types
// ============================================================================

interface Attributes {
	columns: number;
	cardLabelLevel: number;
	buttonTextSignIn: string;
	buttonTextVoucherTerms: string;
	buttonTextCopyCode: string;
	discountCodeLabel: string;
	messageGuest: string;
	messageNotEnrolled: string;
	emptyStateMessage: string;
	emptyStateButtonTextGuest: string;
	emptyStateButtonTextNotEnrolled: string;
	showEnrollmentPrompt: boolean;
	buttonTextColor?: string;
	buttonBackgroundColor?: string;
}

interface CouponRule {
	id: number;
	label: {
		value: string | Record<string, string> | null;
		default?: Record<string, string>;
	};
	creditCost?: { value: number };
	discountValue?: { value: number | string };
	discountType?: { value: string };
	image?: { value: string };
	description?: {
		value: string | Record<string, string> | null;
		default?: Record<string, string>;
	};
	rewardTerms?: {
		value: string | Record<string, string> | null;
		default?: Record<string, string>;
	};
	status: { value: string };
	svg?: string;
}

interface Coupon {
	type: "spend_rule" | "promotion_rule";
	code: string;
	rule: CouponRule;
}

interface GetCouponsResponse {
	spend_rules_coupons: Coupon[];
	promotion_rules_coupons: Coupon[];
}

interface CouponsListViewProps {
	attributes: Attributes;
}

// ============================================================================
// State Components
// ============================================================================

const GuestState: FC<{ attributes: Attributes }> = ({ attributes }) => {
	return (
		<div className="leat-coupons-list__empty-state">
			<p>{attributes.messageGuest}</p>
			<Button
				onClick={() => openAuthModal({ intent: "auto" })}
				variant="contained"
			>
				{attributes.buttonTextSignIn}
			</Button>
		</div>
	);
};

const NotEnrolledState: FC<{ attributes: Attributes }> = ({ attributes }) => {
	const [isJoining, setIsJoining] = useState(false);
	const [joinError, setJoinError] = useState<string | null>(null);

	const handleJoinProgram = async () => {
		setIsJoining(true);
		setJoinError(null);

		try {
			const userId = window.leat?.userId ?? null;
			if (!userId) {
				throw new Error("User ID not found");
			}

			await joinProgram(userId);
			window.location.reload();
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "Failed to join program";
			setJoinError(message);
			setIsJoining(false);
		}
	};

	return (
		<div className="leat-coupons-list__empty-state">
			<p>{attributes.messageNotEnrolled}</p>
			{attributes.showEnrollmentPrompt && (
				<>
					<Button
						onClick={handleJoinProgram}
						disabled={isJoining}
						variant="contained"
					>
						{isJoining
							? "Joining..."
							: attributes.emptyStateButtonTextNotEnrolled}
					</Button>
					{joinError && <p className="leat-coupons-list__error">{joinError}</p>}
				</>
			)}
		</div>
	);
};

// ============================================================================
// Main Component
// ============================================================================

const CouponsListView: FC<CouponsListViewProps> = ({ attributes }) => {
	const {
		status,
		creditsCurrency,
		isLoading: enrollmentLoading,
	} = useEnrollment();
	const [coupons, setCoupons] = useState<Coupon[]>([]);
	const [isLoadingCoupons, setIsLoadingCoupons] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const fetchCoupons = async () => {
			if (enrollmentLoading) {
				return;
			}

			if (status === "guest" || status === "loggedInNotEnrolled") {
				setIsLoadingCoupons(false);
				return;
			}

			try {
				const response = await apiFetch<GetCouponsResponse>({
					path: "/leat/v1/coupons",
				});

				const allCoupons = [
					...response.spend_rules_coupons,
					...response.promotion_rules_coupons,
				].filter(
					(coupon) =>
						coupon.rule.status.value === "publish" &&
						(coupon.rule.label.value || coupon.rule.label.default),
				);

				setCoupons(allCoupons);
				setIsLoadingCoupons(false);
			} catch (err) {
				const isNotEnrolledError =
					err.status === 404 ||
					err.code === "no_contact_uuid" ||
					err.data?.status === 404 ||
					(err.message && err.message.toLowerCase().includes("not found"));

				if (isNotEnrolledError) {
					setCoupons([]);
				} else {
					console.error("[Coupons List] API Error:", err);
					setError(err.message || "Failed to load coupons");
				}
				setIsLoadingCoupons(false);
			}
		};

		fetchCoupons();
	}, [status, enrollmentLoading]);

	if (isLoadingCoupons || enrollmentLoading) {
		return (
			<ItemGridSkeleton
				columns={attributes.columns}
				showMeta={true}
				ariaMessage={__("Loading coupons...", "leat-crm")}
				className="leat-coupons-list__grid"
			/>
		);
	}

	if (error) {
		return (
			<div className="leat-coupons-list__error">
				<p>{error}</p>
			</div>
		);
	}

	if (status === "guest") {
		return <GuestState attributes={attributes} />;
	}

	if (status === "loggedInNotEnrolled") {
		return <NotEnrolledState attributes={attributes} />;
	}

	if (coupons.length === 0 && status === "enrolled") {
		return (
			<div className="leat-coupons-list__empty">
				<p>{attributes.emptyStateMessage}</p>
			</div>
		);
	}

	// Apply Styles-panel color overrides directly — PanelColorSettings returns
	// resolved CSS values (hex or var(...)) so no wrapping needed.
	const buttonInlineStyles: React.CSSProperties = {};
	if (attributes.buttonTextColor) {
		buttonInlineStyles.color = attributes.buttonTextColor;
	}
	if (attributes.buttonBackgroundColor) {
		buttonInlineStyles.backgroundColor = attributes.buttonBackgroundColor;
	}

	const gridStyle: React.CSSProperties = {
		gridTemplateColumns: `repeat(${attributes.columns}, 1fr)`,
	};

	return (
		<div className="leat-coupons-list__grid" style={gridStyle}>
			{coupons.map((coupon, index) => (
				<CouponCard
					key={`${coupon.type}-${coupon.code}-${index}`}
					coupon={coupon}
					creditsCurrency={creditsCurrency ?? "points"}
					labelLevel={attributes.cardLabelLevel}
					discountCodeLabel={attributes.discountCodeLabel}
					buttonTextVoucherTerms={attributes.buttonTextVoucherTerms}
					buttonTextCopyCode={attributes.buttonTextCopyCode}
					buttonInlineStyles={buttonInlineStyles}
				/>
			))}
		</div>
	);
};

// ============================================================================
// Coupon Card Component
// ============================================================================

const CouponCard: FC<{
	coupon: Coupon;
	creditsCurrency: string;
	labelLevel: number;
	discountCodeLabel: string;
	buttonTextVoucherTerms: string;
	buttonTextCopyCode: string;
	buttonInlineStyles?: React.CSSProperties;
}> = ({
	coupon,
	creditsCurrency,
	labelLevel,
	discountCodeLabel,
	buttonTextVoucherTerms,
	buttonTextCopyCode,
	buttonInlineStyles,
}) => {
	const [copied, setCopied] = useState(false);
	const [isTermsOpen, setIsTermsOpen] = useState(false);

	const labelValue = coupon.rule.label.value ?? coupon.rule.label.default;
	const label =
		typeof labelValue === "string" ? labelValue : getTranslatedText(labelValue);

	const descriptionValue =
		coupon.rule.description?.value ?? coupon.rule.description?.default;
	const description = descriptionValue
		? typeof descriptionValue === "string"
			? descriptionValue
			: getTranslatedText(descriptionValue)
		: null;

	const termsValue =
		coupon.rule.rewardTerms?.value ?? coupon.rule.rewardTerms?.default;
	const rewardTerms = termsValue
		? typeof termsValue === "string"
			? termsValue
			: getTranslatedText(termsValue)
		: null;

	const creditCost = coupon.rule.creditCost?.value;
	const discount = coupon.rule.discountValue?.value;
	const replacementVariables = {
		"{{credits}}": creditCost?.toString() || "0",
		"{{credits_currency}}": creditsCurrency,
		"{{discount}}": discount?.toString() || "0",
		"{{coupon}}": coupon.code,
	};

	const displayLabel = replaceStrings(label, [replacementVariables]);
	const displayDescription = description
		? replaceStrings(description, [replacementVariables])
		: null;
	const displayRewardTerms = rewardTerms
		? replaceStrings(rewardTerms, [replacementVariables])
		: null;

	const LabelTag = `h${labelLevel}` as keyof JSX.IntrinsicElements;

	const handleCopy = async () => {
		try {
			if (navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(coupon.code);
			} else {
				const textarea = document.createElement("textarea");
				textarea.value = coupon.code;
				textarea.style.position = "fixed";
				textarea.style.opacity = "0";
				document.body.appendChild(textarea);
				textarea.select();
				document.execCommand("copy");
				document.body.removeChild(textarea);
			}
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error("Failed to copy coupon code:", err);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		}
	};

	return (
		<>
			<Item variant="default">
				<ItemContent>
					<div className="leat-coupon-card__header">
						{coupon.rule.image?.value ? (
							<div className="leat-coupon-card__icon">
								<img src={coupon.rule.image.value} alt="" />
							</div>
						) : coupon.rule.svg ? (
							<div
								className="leat-coupon-card__icon"
								// eslint-disable-next-line react/no-danger
								dangerouslySetInnerHTML={{ __html: coupon.rule.svg }}
							/>
						) : (
							<div
								className="leat-coupon-card__icon leat-coupon-card__icon--placeholder"
								aria-hidden="true"
							/>
						)}
						<div className="leat-coupon-card__title-row">
							<LabelTag className="leat-coupon-card__title">
								{displayLabel}
							</LabelTag>
							{displayRewardTerms && (
								<button
									type="button"
									className="leat-coupon-card__terms-link"
									onClick={() => setIsTermsOpen(true)}
									aria-label={buttonTextVoucherTerms}
								>
									{buttonTextVoucherTerms}
								</button>
							)}
						</div>
					</div>

					<div className="leat-coupon-card__discount-section">
						<div className="leat-coupon-card__discount-info">
							<span className="leat-coupon-card__discount-label">
								{discountCodeLabel}
							</span>
							<code className="leat-coupon-card__discount-code">
								{coupon.code}
							</code>
						</div>
						<Button
							variant="contained"
							className="leat-coupon-card__copy-button leat-button--pill"
							onClick={handleCopy}
							style={buttonInlineStyles}
							aria-label={
								copied ? __("Copied!", "leat-crm") : buttonTextCopyCode
							}
						>
							{copied ? __("Copied!", "leat-crm") : buttonTextCopyCode}
						</Button>
					</div>
				</ItemContent>
			</Item>

			{displayRewardTerms && (
				<Modal
					isOpen={isTermsOpen}
					onClose={() => setIsTermsOpen(false)}
					title={buttonTextVoucherTerms}
					className="leat-coupon-terms-modal"
				>
					<p className="leat-coupon-terms-modal__body">{displayRewardTerms}</p>
				</Modal>
			)}
		</>
	);
};

// ============================================================================
// Initialization
// ============================================================================

export function initCouponsListBlocks(): void {
	if (
		typeof window.wp === "undefined" ||
		typeof window.wp.data === "undefined"
	) {
		setTimeout(initCouponsListBlocks, 100);
		return;
	}

	const blocks = document.querySelectorAll<HTMLElement>(
		'[data-block-type="coupons-list"]',
	);

	if (blocks.length === 0) {
		return;
	}

	for (const block of blocks) {
		const attributes: Attributes = {
			columns: Number.parseInt(block.dataset.columns || "2", 10),
			cardLabelLevel: Number.parseInt(block.dataset.cardLabelLevel || "4", 10),
			buttonTextSignIn: block.dataset.buttonTextSignIn || "Sign In",
			buttonTextVoucherTerms:
				block.dataset.buttonTextVoucherTerms || "Reward terms",
			buttonTextCopyCode: block.dataset.buttonTextCopyCode || "Copy code",
			discountCodeLabel: block.dataset.discountCodeLabel || "Discount code",
			messageGuest:
				block.dataset.messageGuest || "Please sign in to view your coupons",
			messageNotEnrolled:
				block.dataset.messageNotEnrolled ||
				"Join our program to earn exclusive coupons",
			emptyStateMessage:
				block.dataset.emptyStateMessage || "You don't have any coupons yet",
			emptyStateButtonTextGuest:
				block.dataset.emptyStateButtonTextGuest || "Sign In",
			emptyStateButtonTextNotEnrolled:
				block.dataset.emptyStateButtonTextNotEnrolled || "Join Program",
			showEnrollmentPrompt: block.dataset.showEnrollmentPrompt !== "false",
			buttonTextColor: block.dataset.buttonTextColor || undefined,
			buttonBackgroundColor: block.dataset.buttonBackgroundColor || undefined,
		};

		const root = createRoot(block);
		root.render(<CouponsListView attributes={attributes} />);
	}
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initCouponsListBlocks);
} else {
	initCouponsListBlocks();
}
